package diana.soleil.design.iraniancanadienbazzar.core.designsystem.component

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme

/**
 * A selectable city/region option. [id] null means "All Canada". [enabled] false renders the row as a
 * dimmed, non-selectable "coming soon" city (gated by Remote Config `active_launch_cities`).
 */
data class CityOption(val id: String?, val label: String, val enabled: Boolean = true)

/**
 * Compact metro/region picker: a tappable label (location pin + name + chevron) that opens a sheet of
 * [options]. Copy comes from the caller (localized). Selecting an enabled option calls [onSelect] with
 * its id; disabled options show [comingSoonLabel] and can't be selected.
 */
@Composable
fun CitySelector(
    selectedLabel: String,
    options: List<CityOption>,
    selectedId: String?,
    onSelect: (String?) -> Unit,
    sheetTitle: String,
    comingSoonLabel: String,
    modifier: Modifier = Modifier,
    // Single content color for pin/label/chevron when the selector sits on a colored band
    // (e.g. Home's primary header); null keeps the default on-background styling.
    contentColor: Color? = null,
    // Optional content pinned above the option list inside the sheet (e.g. a country toggle that
    // re-scopes [options]). Copy/logic stay with the caller — this component owns no such vocabulary.
    header: (@Composable ColumnScope.() -> Unit)? = null,
) {
    var showSheet by remember { mutableStateOf(false) }

    Row(
        modifier = modifier.clickable { showSheet = true },
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.xxs),
    ) {
        AppIcon(
            name = "location_on",
            size = AppTheme.sizes.iconSm,
            tint = contentColor ?: AppTheme.colors.primary,
        )
        Text(
            text = selectedLabel,
            style = AppTheme.typography.bodySm,
            color = contentColor ?: AppTheme.colors.onSurfaceVariant,
        )
        AppIcon(
            name = "expand_more",
            size = AppTheme.sizes.iconSm,
            tint = contentColor ?: AppTheme.colors.onSurfaceVariant,
        )
    }

    if (showSheet) {
        AppBottomSheet(onDismissRequest = { showSheet = false }, title = sheetTitle) {
            header?.invoke(this)
            options.forEach { option ->
                val isSelected = option.id == selectedId
                val rowModifier = Modifier
                    .fillMaxWidth()
                    .then(
                        if (option.enabled) {
                            Modifier.clickable {
                                onSelect(option.id)
                                showSheet = false
                            }
                        } else {
                            Modifier
                        },
                    )
                    .padding(vertical = AppTheme.spacing.md, horizontal = AppTheme.spacing.lg)
                Row(
                    modifier = rowModifier,
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
                ) {
                    val contentColor = when {
                        !option.enabled -> AppTheme.colors.onSurfaceVariant
                        isSelected -> AppTheme.colors.primary
                        else -> AppTheme.colors.onSurface
                    }
                    AppIcon(
                        name = if (isSelected) "check_circle" else "location_city",
                        tint = if (isSelected) AppTheme.colors.primary else AppTheme.colors.onSurfaceVariant,
                    )
                    Text(
                        text = option.label,
                        style = AppTheme.typography.bodyLg,
                        color = contentColor,
                        modifier = Modifier.weight(1f),
                    )
                    if (!option.enabled) {
                        StatusBadge(label = comingSoonLabel)
                    }
                }
            }
        }
    }
}
